import { documentsToMove, useLoad, type MemberKind, type Workspace, type WorkspaceInput, type WorkspaceMember } from '@fiveox/prodoc-ui'
import {
  Alert,
  Badge,
  Button,
  ConfirmDialog,
  DataTable,
  Dialog,
  EmptyState,
  Input,
  PageHeader,
  Select,
  Skeleton,
  Textarea,
  useToast,
  type ColumnDef,
} from '@fiveox/ui'
import { Archive, ExternalLink, Plus } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'

import { useOrg } from '../../components/org-context'
import { WORKSPACE } from '../../lib/access'
import { ApiError, errorMessage, prodoc } from '../../lib/api'
import { CONSOLE_URL } from '../../lib/config'
import { fetchOrgMembers, type OrgMember } from '../../lib/orgs'
import { AboutPopover, DialogFooter, FormDialog, changesNote, plural, useDraft } from './admin-bits'

const muted = (text: string) => <span className="text-muted-foreground">{text}</span>

/** *iTax’s workspace* — what marks an app's own (FR-61). */
export const appWorkspaceLabel = (app: { label: string }) => `${app.label}’s workspace`

/** W26 (FR-62): the service's `app_workspace`, in the screen's words. */
const APP_WORKSPACE_KEPT = 'An app’s own workspace keeps its name and is never archived — its app files into it. You can still choose who sees it.'

/**
 * Admin → Workspaces — Finance, Purchase, Stores… the org's own filing units
 * below it (constitution §3, spec FR-12): where documents are filed, and whose
 * sight of them is narrowed.
 *
 * **Every document is in a workspace** (D9). The organisation's **default**
 * workspace is made at first use, holds whatever nobody filed elsewhere, is
 * seen by every member with `can_view`, and is **never archived** — it is
 * marked here and offers no archive. A workspace is archived, never deleted,
 * and not while documents are filed in it unless the call names another
 * workspace they move to: when the service answers `CONFLICT` with
 * `details.document_count`, the dialog asks where they go. There is no
 * "no workspace" any more.
 *
 * **Members here are visibility, never permission** (plan §4.2): a `member` or
 * `granted` row says whose documents this person sees; what they may *do* is
 * `core-access-management`'s, set per member in the FiveOX console. The people
 * are **picked from the organisation's member list** — core-org-management's
 * directory, read, never copied (FR-13) — rather than typed as ids.
 *
 * **W22 (FR-61, ADR-0018): an app's own workspace.** A sibling app that files
 * without naming a workspace files into its own — *iTax*, *ProGST*, made at
 * its first filing — instead of the default. The screen marks it (*iTax’s
 * workspace*, from the service's `app`, never from the name), says that only
 * administrators see it until people are added, and that a person always sees
 * what they uploaded, through the app or not.
 *
 * **W26 (FR-62, ADR-0019): it is the app's.** It is never renamed, re-described
 * or archived — the service refuses (`app_workspace`), and the screen offers
 * neither *Edit* nor *Archive* on it; what an administrator does with it is
 * choose who sees it. **W26 (FR-63): the screen at a glance.** The intro is one
 * line, its explanation behind an ⓘ (a popover, so a click — a phone too), and
 * *Who sees it* is the column alone: the people icon left the actions.
 */
export function WorkspacesPage() {
  const { toast } = useToast()
  const { can } = useOrg()
  const workspaces = useLoad(() => prodoc.workspaces.list(), [])
  // Ids, not rows: after a reload the dialogs read the fresh counts.
  const [editing, setEditing] = useState<string | 'new' | null>(null)
  const [archiving, setArchiving] = useState<string | null>(null)
  const [members, setMembers] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const list = workspaces.data
  const byId = (id: string | null) => list?.find((w) => w.id === id) ?? null
  const canArchive = can(WORKSPACE, 'can_delete')

  const close = () => {
    setEditing(null)
    setArchiving(null)
    setMembers(null)
    setToMove(null)
    setError('')
  }

  const save = async (input: WorkspaceInput) => {
    setBusy(true)
    setError('')
    try {
      const saved = editing === 'new' || !editing ? await prodoc.workspaces.create(input) : await prodoc.workspaces.update(editing, input)
      await workspaces.reload()
      setEditing(null)
      toast(editing === 'new' ? `${saved.name} added.` : `${saved.name} saved.`)
    } catch (e) {
      setError(e instanceof ApiError && e.reason === 'app_workspace' ? APP_WORKSPACE_KEPT : errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  /** How many documents the service said need somewhere to go, for the workspace being archived. */
  const [toMove, setToMove] = useState<number | null>(null)

  const archive = async (ws: Workspace, moveTo: string | undefined) => {
    setBusy(true)
    setError('')
    try {
      await prodoc.workspaces.archive(ws.id, moveTo === undefined ? {} : { move_to: moveTo })
      setArchiving(null)
      setToMove(null)
      const target = moveTo ? byId(moveTo) : null
      toast(target ? `${ws.name} archived. Its documents moved to ${target.name}.` : `${ws.name} archived.`)
      await workspaces.reload()
    } catch (e) {
      // Documents are filed in it (possibly since the list was read): the
      // service names how many, and the dialog asks where they go.
      const reason = e instanceof ApiError ? e.reason : null
      const count = documentsToMove(e)
      if (reason === 'default_workspace') setError('The default workspace is never archived — it holds whatever nobody filed elsewhere.')
      else if (reason === 'app_workspace') setError(APP_WORKSPACE_KEPT)
      else if (count !== null) setToMove(count)
      else setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const add = (
    <Button onClick={() => setEditing('new')} disabled={!list}>
      <Plus size={18} strokeWidth={1.75} aria-hidden />
      Add workspace
    </Button>
  )

  const columns: ColumnDef<Workspace>[] = [
    {
      key: 'name',
      label: 'Workspace',
      width: 300,
      render: (w) => (
        <div className="max-w-96 min-w-0">
          <span className="text-foreground flex flex-wrap items-center gap-2 font-medium">
            {w.name}
            {w.is_default && (
              <Badge size="sm" tone="info">
                Default
              </Badge>
            )}
            {w.app && (
              <Badge size="sm" tone="neutral">
                {appWorkspaceLabel(w.app)}
              </Badge>
            )}
          </span>
          {w.is_default ? (
            <p className="text-muted-foreground mt-0.5 text-xs whitespace-normal">Where an upload lands when nobody chose a workspace. Every member sees it.</p>
          ) : w.app ? (
            <p className="text-muted-foreground mt-0.5 text-xs whitespace-normal">
              {w.description ? `${w.description} · ` : ''}Where {w.app.label} files what it stores without naming a workspace. Administrators see it, and
              whoever you add; a person always sees what they uploaded.
            </p>
          ) : (
            w.description && <p className="text-muted-foreground mt-0.5 line-clamp-2 text-xs whitespace-normal">{w.description}</p>
          )}
        </div>
      ),
    },
    {
      key: 'members',
      label: 'Who sees it',
      width: 160,
      render: (w) =>
        w.is_default ? (
          muted('Every member')
        ) : (
          <Button variant="link" size="sm" onClick={() => setMembers(w.id)}>
            {w.member_count === undefined
              ? 'See who'
              : w.member_count
                ? plural(w.member_count, 'person', 'people')
                : // An app's own starts with administrators only, until people are added (FR-61).
                  w.app
                  ? 'Administrators only'
                  : 'Nobody yet'}
          </Button>
        ),
    },
    {
      key: 'documents',
      label: 'Documents',
      width: 140,
      render: (w) =>
        w.document_count === 0 ? (
          muted('None')
        ) : (
          <Link to={`/documents?workspace=${w.id}`} className="text-primary hover:underline">
            {w.document_count === undefined ? 'Open its documents' : plural(w.document_count, 'document')}
          </Link>
        ),
    },
  ]

  return (
    <div className="space-y-6">
      <PageHeader
        title="Workspaces"
        description={
          // W26 (FR-63): one line, its *FiveOX console* the console's link; the rest behind the ⓘ.
          <span className="inline-flex flex-wrap items-center gap-x-1">
            <span>
              Who sees which documents. Roles are set in the{' '}
              <a href={`${CONSOLE_URL}/access`} className="text-primary inline-flex items-center gap-1 hover:underline">
                FiveOX console
                <ExternalLink size={12} aria-hidden />
              </a>
              .
            </span>
            <AboutWorkspaces />
          </span>
        }
        actions={add}
      />

      {workspaces.error && (
        <Alert tone="error">
          Could not load workspaces: {workspaces.error}{' '}
          <Button variant="link" onClick={() => void workspaces.reload()}>
            Try again
          </Button>
        </Alert>
      )}

      {list && list.length === 0 ? (
        <EmptyState
          title="No workspaces yet"
          description="The default workspace is made with the first upload. Add the workspaces your organisation works in — Finance, Purchase, Stores — then say who sees each."
          action={add}
        />
      ) : (
        (list || !workspaces.error) && (
          <div className="space-y-2">
            <DataTable
              columns={columns}
              rows={list ?? []}
              rowKey={(w) => w.id}
              loading={!list}
              trailing={{
                header: <span className="sr-only">Actions</span>,
                width: 140,
                // *Who sees it* is its column's (FR-63); an app's own is never edited or archived (FR-62).
                cell: (w) => (
                  <div className="flex items-center justify-end gap-1">
                    {!w.app && (
                      <Button variant="ghost" size="sm" onClick={() => setEditing(w.id)} aria-label={`Edit ${w.name}`}>
                        Edit
                      </Button>
                    )}
                    {canArchive && !w.is_default && !w.app && (
                      <Button variant="ghost" size="sm" onClick={() => setArchiving(w.id)} aria-label={`Archive ${w.name}`} title="Archive">
                        <Archive size={16} strokeWidth={1.75} aria-hidden />
                      </Button>
                    )}
                  </div>
                ),
              }}
            />
            {list && (
              <p className="text-muted-foreground text-xs">
                {plural(list.length, 'workspace')}. Archiving frees a name for reuse; nothing is ever deleted. The default workspace and an app’s own are
                never archived.
              </p>
            )}
          </div>
        )
      )}

      {list && (
        <>
          <WorkspaceDialog
            open={editing === 'new' || byId(editing) !== null}
            workspace={editing === 'new' ? null : byId(editing)}
            workspaces={list}
            busy={busy}
            error={error}
            onClose={close}
            onSave={save}
          />
          <ArchiveDialog
            open={archiving !== null}
            workspace={byId(archiving)}
            workspaces={list}
            toMove={toMove ?? (byId(archiving)?.document_count || null)}
            busy={busy}
            error={error}
            onClose={close}
            onArchive={archive}
          />
          <MembersDialog open={members !== null} workspace={byId(members)} onClose={close} onChanged={() => void workspaces.reload()} />
        </>
      )}
    </div>
  )
}

/* ---------------------------------------------------------------- about */

/** W26 (FR-63, D79): what the screen's one line leaves out, behind the ⓘ (`AboutPopover`, D81). */
function AboutWorkspaces() {
  return (
    <AboutPopover label="About workspaces">
      <p>Documents are filed in a workspace; the people in it see them, and so does anyone an administrator grants it to.</p>
      <p>What nobody files elsewhere is in the default workspace, which every member sees.</p>
      <p>
        An app’s own workspace — iTax’s, ProGST’s — is where that app files. Administrators see it until you add people; it keeps its name and is never
        archived.
      </p>
      <p>
        What people may <i>do</i> — upload, review, archive — is their ProDoc role, set per member in the{' '}
        <a href={`${CONSOLE_URL}/access`} className="text-primary inline-flex items-center gap-1 hover:underline">
          FiveOX console
          <ExternalLink size={12} aria-hidden />
        </a>
        .
      </p>
    </AboutPopover>
  )
}

/* ----------------------------------------------------------- add / edit */

interface Draft {
  name: string
  description: string
}

/** Add a workspace, or edit one: a name and a line saying what it holds. */
function WorkspaceDialog({
  open,
  workspace,
  workspaces,
  busy,
  error,
  onClose,
  onSave,
}: {
  open: boolean
  workspace: Workspace | null
  workspaces: Workspace[]
  busy: boolean
  error: string
  onClose: () => void
  onSave: (input: WorkspaceInput) => void
}) {
  const [d, set] = useDraft<Draft>(open, () => ({
    name: workspace?.name ?? '',
    description: workspace?.description ?? '',
  }))
  const [tried, setTried] = useDraft(open, () => false)

  const others = workspaces.filter((x) => x.id !== workspace?.id)
  const input: WorkspaceInput = { name: d.name.trim(), description: d.description.trim() || null }

  // The service's checks, in its words: a name is unique per org over active rows (FR-12).
  const nameError = !input.name
    ? 'Give the workspace a name.'
    : others.some((o) => o.name.toLowerCase() === input.name.toLowerCase())
      ? `There is already a workspace called ${input.name}.`
      : undefined

  const changes = workspace ? [input.name !== workspace.name, input.description !== (workspace.description ?? null)].filter(Boolean).length : 0

  const submit = () => {
    setTried(true)
    if (!nameError) onSave(input)
  }

  return (
    <FormDialog title={workspace ? `Edit ${workspace.name}` : 'Add a workspace'} open={open} error={error} onClose={onClose} onSubmit={submit}>
      <div className="space-y-5">
        <Input label="Name" placeholder="Finance" value={d.name} error={tried ? nameError : undefined} onChange={(e) => set({ ...d, name: e.target.value })} />
        <Textarea
          label="Description"
          labelAside={<span className="text-muted-foreground text-xs">Optional</span>}
          rows={2}
          placeholder="What it holds — supplier invoices and payments, say."
          value={d.description}
          onChange={(e) => set({ ...d, description: e.target.value })}
        />
      </div>
      {workspace ? (
        <DialogFooter busy={busy} onClose={onClose} note={changesNote(changes)} disabled={changes === 0} />
      ) : (
        <DialogFooter busy={busy} onClose={onClose} submitLabel="Add workspace" busyLabel="Adding…" />
      )}
    </FormDialog>
  )
}

/* -------------------------------------------------------------- archive */

/** Archive a workspace. With nothing to move, it is a plain confirmation. With
 *  documents — known from the list, or from the service's refusal — the person
 *  first chooses which workspace they go to; the default one is always there
 *  to take them. Nothing is deleted either way. */
function ArchiveDialog({
  open,
  workspace,
  workspaces,
  toMove,
  busy,
  error,
  onClose,
  onArchive,
}: {
  open: boolean
  workspace: Workspace | null
  workspaces: Workspace[]
  /** Documents that need somewhere to go; `null` when none are known to. */
  toMove: number | null
  busy: boolean
  error: string
  onClose: () => void
  onArchive: (workspace: Workspace, moveTo: string | undefined) => void
}) {
  const others = workspaces.filter((x) => x.id !== workspace?.id && x.status === 'active')
  const fallback = others.find((o) => o.is_default)
  const [moveTo, setMoveTo] = useDraft(open, () => fallback?.id ?? '')
  if (!workspace) return null

  const target = others.find((o) => o.id === moveTo)

  if (!toMove) {
    return (
      <ConfirmDialog
        open={open}
        onClose={onClose}
        title={`Archive ${workspace.name}?`}
        confirmLabel="Archive workspace"
        cancelLabel={`Keep ${workspace.name}`}
        tone="danger"
        busy={busy}
        busyLabel="Archiving…"
        error={error}
        onConfirm={() => onArchive(workspace, undefined)}
      >
        <p>Its name is free to use again; the record stays. If documents are filed in it, you are asked where they go first.</p>
      </ConfirmDialog>
    )
  }

  return (
    <FormDialog title={`Archive ${workspace.name}?`} open={open} error={error} onClose={onClose} onSubmit={() => moveTo && onArchive(workspace, moveTo)}>
      <div className="space-y-4">
        <p className="text-muted-foreground text-sm">
          {workspace.name} holds {plural(toMove, 'document')}. A workspace is never archived with documents in it — choose the workspace they move to
          first.
        </p>
        <Select label="Move them to" value={moveTo} onChange={(e) => setMoveTo(e.target.value)}>
          <option value="" disabled>
            Choose…
          </option>
          {others.map((o) => (
            <option key={o.id} value={o.id}>
              {o.name}
              {o.is_default ? ' (default)' : ''}
            </option>
          ))}
        </Select>
        {target && (
          <ul className="text-muted-foreground list-disc space-y-1 pl-5 text-xs">
            <li>
              Its documents move to {target.name}
              {target.is_default ? ', the default workspace, which every member sees.' : `, and only people who see ${target.name} see them.`}
            </li>
            <li>Nothing is deleted. The workspace’s name is free to use again.</li>
          </ul>
        )}
      </div>
      <DialogFooter
        busy={busy}
        onClose={onClose}
        tone="danger"
        submitLabel={`Archive ${workspace.name}`}
        busyLabel="Archiving…"
        disabled={!moveTo}
        note={moveTo ? undefined : 'Choose where they go first.'}
      />
    </FormDialog>
  )
}

/* -------------------------------------------------------------- members */

const KIND_LABEL: Record<MemberKind, string> = { member: 'Member', granted: 'Granted sight' }

/** "Asha Rao — asha@example.in", or whatever of it the directory has. */
const personLabel = (m: Pick<OrgMember, 'full_name' | 'email'> | undefined, fallback: string) =>
  m ? [m.full_name, m.email].filter(Boolean).join(' — ') || fallback : fallback

/**
 * Who sees a workspace's documents. `member` is someone who works in it;
 * `granted` is someone an administrator let see it as well. Neither is a
 * permission. People are chosen from the organisation's own member list
 * (core-org-management), and named from it.
 */
function MembersDialog({
  open,
  workspace,
  onClose,
  onChanged,
}: {
  open: boolean
  workspace: Workspace | null
  onClose: () => void
  onChanged: () => void
}) {
  const { toast } = useToast()
  const { org } = useOrg()
  const id = workspace?.id ?? null
  const members = useLoad(() => (id && open ? prodoc.workspaces.members.list(id) : Promise.resolve([] as WorkspaceMember[])), [id, open])
  const people = useLoad(() => (open ? fetchOrgMembers(org.id) : Promise.resolve([] as OrgMember[])), [org.id, open])
  const [userId, setUserId] = useDraft(open, () => '')
  const [kind, setKind] = useDraft<MemberKind>(open, () => 'member')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  if (!workspace) return null

  const directory = new Map((people.data ?? []).map((p) => [p.user_id, p]))
  const already = new Set((members.data ?? []).map((m) => m.user_id))
  const choices = (people.data ?? []).filter((p) => !already.has(p.user_id))
  const nameOf = (m: WorkspaceMember) => m.name ?? directory.get(m.user_id)?.full_name ?? directory.get(m.user_id)?.email ?? 'A former member'

  const run = async (fn: () => Promise<void>, done: string) => {
    setBusy(true)
    setError('')
    try {
      await fn()
      await members.reload()
      onChanged()
      toast(done)
    } catch (e) {
      setError(errorMessage(e))
    } finally {
      setBusy(false)
    }
  }

  const add = () => {
    if (!userId) {
      setError('Choose a person from the organisation.')
      return
    }
    const who = personLabel(directory.get(userId), 'They')
    void run(async () => {
      await prodoc.workspaces.members.put(workspace.id, userId, kind)
      setUserId('')
    }, `${who} now ${who === 'They' ? 'see' : 'sees'} ${workspace.name}.`)
  }

  return (
    <Dialog open={open} onClose={onClose} title={`Who sees ${workspace.name}`} error={error} width="lg">
      <div className="space-y-5">
        <p className="text-muted-foreground text-sm">
          Everyone listed sees this workspace’s documents. What they may do with them is their ProDoc role, set in the{' '}
          <a href={`${CONSOLE_URL}/access`} className="text-primary hover:underline">
            FiveOX console
          </a>
          , where the organisation’s members are managed.
        </p>

        {members.error && <Alert tone="error">Could not load the list: {members.error}</Alert>}

        <ul className="divide-border divide-y">
          {members.loading && !members.data && (
            <li className="py-2">
              <Skeleton className="h-5 w-2/3" />
            </li>
          )}
          {(members.data ?? []).map((m) => (
            <li key={m.user_id} className="flex flex-wrap items-center gap-3 py-2">
              <div className="min-w-0 flex-1">
                <p className="text-foreground truncate text-sm font-medium">{nameOf(m)}</p>
                <p className="text-muted-foreground truncate text-xs">{m.email ?? directory.get(m.user_id)?.email ?? 'No longer in the organisation’s list'}</p>
              </div>
              <Badge size="sm" tone={m.kind === 'member' ? 'info' : 'neutral'}>
                {KIND_LABEL[m.kind]}
              </Badge>
              <Button
                variant="ghost"
                size="sm"
                disabled={busy}
                onClick={() => void run(async () => void (await prodoc.workspaces.members.remove(workspace.id, m.user_id)), `${nameOf(m)} no longer sees ${workspace.name}.`)}
                aria-label={`Remove ${nameOf(m)}`}
              >
                Remove
              </Button>
            </li>
          ))}
          {members.data && members.data.length === 0 && (
            <li className="text-muted-foreground py-2 text-sm">Nobody yet — only people with sight of every workspace see these documents.</li>
          )}
        </ul>

        {people.error ? (
          <Alert tone="error">
            Could not read the organisation’s members: {people.error}{' '}
            <Button variant="link" onClick={() => void people.reload()}>
              Try again
            </Button>
          </Alert>
        ) : (
          <form
            noValidate
            className="grid items-end gap-3 sm:grid-cols-[minmax(0,1fr)_11rem_auto]"
            onSubmit={(e) => {
              e.preventDefault()
              add()
            }}
          >
            <Select
              label="Person"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              disabled={people.loading || choices.length === 0}
              hint={!people.loading && choices.length === 0 ? 'Everyone in the organisation already sees it.' : undefined}
            >
              <option value="">{people.loading ? 'Loading the organisation’s members…' : 'Choose a member…'}</option>
              {choices.map((p) => (
                <option key={p.user_id} value={p.user_id}>
                  {personLabel(p, p.user_id)}
                </option>
              ))}
            </Select>
            <Select label="As" value={kind} onChange={(e) => setKind(e.target.value as MemberKind)}>
              <option value="member">Member</option>
              <option value="granted">Granted sight</option>
            </Select>
            <Button type="submit" loading={busy} loadingText="Adding…" disabled={!userId}>
              Add
            </Button>
          </form>
        )}
      </div>
    </Dialog>
  )
}
